import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/* ── Models (mapped from the available-slots API response) ─────────────── */
export interface Slot {
  id: string;                     // slot_id
  label: string;                  // start_time, e.g. "05:00 PM"
  range: string;                  // time_range
  available: boolean;
  reason?: string;                // ui_reason
  period: 'Morning' | 'Afternoon' | 'Evening';
}
/** Lightweight row shown in the search dropdown */
export interface DoctorSummary {
  doctor_code: string;
  name: string;
  specialty: string;
  hospital: string;
  profilepic_url?: string;
}

export interface SlotDay { date: string; label: string; total: number; slots: Slot[]; }

export interface Doctor {
  id: string;                     // doctor code, used as the path id
  doctor_code: string;
  name: string;
  profilepic_url?: string;
  specialty: string;
  hospital: string;               // clinic_name
  address?: string;
  experience_label?: string;      // "8 yrs"
  available_today?: boolean;
  consultation_fee: number;
  fee_symbol: string;
  specializations?: string[];
  languages?: string[];
  education?: string[];
  bio?: string;
  days: SlotDay[];          // availability.slots, already loaded
}

export interface PricingLineItem {
  key: string;
  label: string;
  amount: number;
  type: 'charge' | 'tax' | 'discount' | string;
  refundable: boolean | null;
}

export interface Pricing {
  context: string;
  currency: string;
  line_items: PricingLineItem[];
  totals: {
    paid_amount: number;
    final_amount: number;
    final_amount_label: string;
  };
}
export interface BookingOrder {  razorpay_order_id: string;   razorpay_payment_id: any }
export interface BookingPayload {
  user_id: any;
  appointment_id: string;
  lawyer_id: string;
  amount: number;
  slot_id: any;
}
export interface VerifyPayload { booking_id: string; razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string; }

@Injectable({ providedIn: 'root' })
export class DoctorService {
  constructor(private http: HttpClient) {}

  private headers(): HttpHeaders {
    return new HttpHeaders({ Authorization: 'Bearer ' + localStorage.getItem('TOKEN') + ' ' + localStorage.getItem('PK') });
  }

  /**
   * REAL endpoint: GET professional/v3/lawyers/{doctor_code}/available-slots
   * Returns the doctor (or null when "Doctor not found.") with profile + slots mapped.
   */
  lookupDoctor(code: string): Observable<Doctor | null> {
    return this.http.get<any>(
      `${environment.cofferRestUrl}professional/v3/lawyers/${encodeURIComponent(code)}/available-slots`,
      { headers: this.headers() }
    ).pipe(
      map(res => (res?.error || !res?.data?.profile) ? null : this.mapDoctor(res.data)),
      catchError(err => {
        const msg = (err?.error?.msg || '').toLowerCase();
        return (err?.status === 404 || msg.includes('not found')) ? of(null) : throwError(() => err);
      })
    );
  }

  private mapDoctor(data: any): Doctor {
    const p = data.profile;
    const days: SlotDay[] = (data.availability?.slots || []).map((d: any) => {
      const mk = (arr: any[], period: Slot['period']): Slot[] => (arr || []).map(s => ({
        id: s.slot_id, label: s.start_time, range: s.time_range,
        available: s.available !== false, reason: s.ui_reason, period,
      }));
      return {
        date: d.date, label: d.day_label, total: d.total_slots ?? 0,
        slots: [...mk(d.morning, 'Morning'), ...mk(d.afternoon, 'Afternoon'), ...mk(d.evening, 'Evening')],
      } as SlotDay;
    });
    const text = (o: any) => (typeof o === 'string' ? o : Object.values(o || {}).filter(v => typeof v === 'string' && v).join(', '));
    return {
      id: p.lawyer_id, doctor_code: p.doctor_code, name: p.name,
      profilepic_url: p.profile_pic_url || undefined,
      specialty: p.specialisations?.[0] || '',
      hospital: p.clinic_name || '',
      address: text(p.address) || undefined,
      experience_label: p.experience?.label,
      available_today: days[0]?.label === 'Today' && days[0].slots.some(s => s.available),
      consultation_fee: Number(p.consultation_fee?.amount) || 0,
      fee_symbol: p.consultation_fee?.symbol || '₹',
      specializations: p.specialisations || [],
      languages: p.languages_spoken || [],
      education: (p.education || []).map((e: any) => [e.degree, e.university].filter(Boolean).join(' – ')).filter(Boolean),
      bio: p.bio,
      days,
    };
  }

  /* ── TODO: confirm these endpoints with your backend (not in the API you shared) ── */
  private readonly base = `${environment.cofferRestUrl}`;
 


  private readonly paybase = `${environment.paymentUrl}`;

  getPricing(doctorId: number | string, fee: number, slotId: number | string, coupon?: string): Observable<Pricing> {
    // let params = new HttpParams();
    // if (coupon) params = params.set('coupon_code', coupon);
    return this.http.post<any>(`${this.paybase}payment/v1/checkout/preview`, { 
    "amount": fee
 });
  }

  createAppointment(payload: { lawyer_id: string; slot_id: string }): Observable<any> {
  return this.http.post<any>(
    `${this.base}consumer/v3/appointments`,
    payload,
    { headers: this.headers() }
  );
}
  
  createBooking(payload: BookingPayload): Observable<any> { return this.http.post<any>(
    `${this.paybase}payment/v1/order`,
    payload,
    { headers: this.headers() }
  );
}
  verifyPayment(payload: any): Observable<any> {
    return this.http.post<any>(`${this.paybase}payment/v1/verify`, payload, { headers: this.headers() });
  }

  loadRazorpay(): Promise<boolean> {
    if ((window as any).Razorpay) return Promise.resolve(true);
    return new Promise(resolve => {
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });
  }

  searchDoctorsByName(q: string, limit = 10): Observable<DoctorSummary[]> {
    const params = new HttpParams().set('q', q).set('limit', limit);
    return this.http.get<any>(`${environment.cofferRestUrl}professional/v3/doctors/search`, { params, headers: this.headers() }).pipe(
      map(res => {
        if (res?.error) return [];
        const list: any[] = res?.data?.doctors || res?.doctors || [];
        return list.map(d => ({
          doctor_code: d.doctor_code,
          name: d.name,
          specialty: d.specialisations?.[0] || d.specialty || '',
          hospital: d.clinic_name || d.hospital || '',
          profilepic_url: d.profile_pic_url || d.profilepic_url || undefined,
        } as DoctorSummary));
      })
    );
  }
  
}